// pages/UserSigninFormPage.tsx

import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import { AppProvider } from '@/contexts/AppContext';
import { usePageContext } from '@/core/hooks/page-context';
import { BasicResponse } from '@/core/models/common-types';

import UserSigninForm from '../components/UserSigninForm';

type SigninMessageKey = 'password-reset-success' | 'reset-link-expired' | 'invalid-reset-link';

const messageMap: Record<SigninMessageKey, BasicResponse> = {
  'password-reset-success': { status: true, info: 'Your password has been reset successfully. Please sign in.' },
  'reset-link-expired': { status: false, info: 'Your password reset link has expired. Please request a new one.' },
  'invalid-reset-link': { status: false, info: 'Invalid password reset link. Please request a new one.' },
};

const isSigninMessageKey = (value: string): value is SigninMessageKey => value in messageMap;

interface UserSigninPageContext {
  loginSessionId: string;
}

const UserSigninFormPage = () => {
  const params = new URLSearchParams(window.location.search);
  const key = params.get('message') ?? params.get('error');
  const initialMessage: BasicResponse | null = key && isSigninMessageKey(key) ? messageMap[key] : null;
  const { loginSessionId } = usePageContext<UserSigninPageContext>();

  useEffect(() => {
    if (key) {
      const url = new URL(window.location.href);
      url.searchParams.delete('message');
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url);
    }
  }, []);

  return (
    <AppProvider>
      <ErrorBoundary>
        <UserSigninForm loginSessionId={loginSessionId} initialMessage={initialMessage} />
      </ErrorBoundary>
    </AppProvider>
  );
};

const root = createRoot(document.getElementById('UserSigninFormPage')!);

root.render(<UserSigninFormPage />);